import { useSEO } from "../../hooks/useSEO";
import { seoConfig } from "../../utils/seoConfig";
import { WanderfulHero } from './WanderfulHero';
import { Navbar, Footer } from './LandingPage';

const CoachingPage = () => {
  useSEO(seoConfig.coaching);
  return (
    <div className="bg-white min-h-screen">
      <Navbar />
      <WanderfulHero
        title="Stop spending your nights marking papers."
        subtitle="Grade entire test series in hours, not weeks. Get detailed reports ready to share with students the same day."
        cta1Text="Get Started"
        cta2Text="Talk to our team"
        cta2Link="/support"
      />
      
      {/* Specific Sections based on user request */}
      <section className="py-8 sm:py-12 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="bg-[#f9f9f9] rounded-2xl sm:rounded-[24px] md:rounded-[40px] p-4 sm:p-6 md:p-8 lg:p-16">
            <div className="text-center mb-12">
              <span className="text-sm font-bold text-slate-900 uppercase tracking-widest bg-gray-100 px-4 py-1.5 rounded-full">Exams We Support</span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-black mt-4 mb-4">Built for India's Toughest Exams</h2>
              <p className="text-gray-600 max-w-2xl mx-auto font-medium">We know exactly how UPSC, JEE, NEET, and CA exams are marked. Your rubric. Your standards.</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
              <div className="bg-white p-4 sm:p-6 rounded-2xl border border-gray-100 shadow-sm hover:border-black hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer">
                <div className="w-8 h-8 rounded-full bg-gray-100 text-slate-900 flex items-center justify-center font-bold text-sm mb-4">01</div>
                <h3 className="font-bold text-lg mb-2 text-slate-900">UPSC Mains</h3>
                <p className="text-gray-600 text-sm">Essays marked like an actual UPSC examiner would. We check for thesis clarity, examples, and analytical depth.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:border-black hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer">
                <div className="w-8 h-8 rounded-full bg-gray-100 text-slate-900 flex items-center justify-center font-bold text-sm mb-4">02</div>
                <h3 className="font-bold text-lg mb-2 text-slate-900">JEE / NEET</h3>
                <p className="text-gray-600 text-sm">Partial credit for method, not just answers. Perfect for physics and chemistry problem solving.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:border-black hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer">
                <div className="w-8 h-8 rounded-full bg-gray-100 text-slate-900 flex items-center justify-center font-bold text-sm mb-4">03</div>
                <h3 className="font-bold text-lg mb-2 text-slate-900">CA / CS / CMA</h3>
                <p className="text-gray-600 text-sm">Complex answers with multiple valid approaches? We handle them. Match our marking to yours.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:border-black hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer">
                <div className="w-8 h-8 rounded-full bg-gray-100 text-slate-900 flex items-center justify-center font-bold text-sm mb-4">04</div>
                <h3 className="font-bold text-lg mb-2 text-slate-900">State PCS</h3>
                <p className="text-gray-600 text-sm">Regional variations matter. Set your own standards and we'll evaluate every paper by them.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Emphasis as Bento Grid (Dark Mode) */}
      <section className="py-8 sm:py-12 md:py-24 bg-black text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <span className="text-sm font-bold text-white/50 uppercase tracking-widest">Why Institutes Switch</span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mt-4 mb-4">Results Your Students Actually Trust</h2>
            <p className="text-gray-400 max-w-2xl mx-auto font-medium">Faster results. Same quality. Now everyone focuses on learning, not logistics.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
            {/* Card 1: Large */}
            <div className="md:col-span-2 p-6 sm:p-8 lg:p-10 bg-neutral-900 rounded-2xl sm:rounded-3xl border border-neutral-800 hover:border-neutral-700 transition-all duration-300 flex flex-col justify-between group cursor-pointer">
              <div>
                <h3 className="font-bold text-2xl mb-3 text-white">Results in Hours, Not Weeks</h3>
                <p className="text-neutral-400 text-sm leading-relaxed max-w-md">Done with your test series Friday? Results are ready Saturday morning. Students see their mistakes while everything's fresh. No more waiting.</p>
              </div>
              <span className="text-8xl font-black text-neutral-800 group-hover:text-neutral-700 transition-colors">01</span>
            </div>

            {/* Card 2: Tall */}
            <div className="p-6 sm:p-8 lg:p-10 bg-neutral-900 rounded-2xl sm:rounded-3xl border border-neutral-800 hover:border-neutral-700 transition-all duration-300 flex flex-col justify-between group cursor-pointer">
              <div>
                <h3 className="font-bold text-2xl mb-3 text-white">Your Faculty Can Actually Teach</h3>
                <p className="text-neutral-400 text-sm leading-relaxed">No more nights spent marking papers. They run doubt sessions, give feedback, improve classes. The work they actually want to do.</p>
              </div>
              <span className="text-8xl font-black text-neutral-800 group-hover:text-neutral-700 transition-colors">02</span>
            </div>

            {/* Card 3: Wide */}
            <div className="md:col-span-3 p-10 bg-neutral-900 rounded-3xl border border-neutral-800 hover:border-neutral-700 transition-all duration-300 flex flex-col md:flex-row md:items-center justify-between group cursor-pointer">
              <div className="max-w-xl">
                <h3 className="font-bold text-2xl mb-3 text-white">See Which Topics Are Failing</h3>
                <p className="text-neutral-400 text-sm leading-relaxed">Know immediately if your batch struggled with organic chemistry or missed logical reasoning. Fix your teaching before the next test.</p>
              </div>
              <span className="text-8xl font-black text-neutral-800 group-hover:text-neutral-700 transition-colors">03</span>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Emphasis */}
      <section className="py-8 sm:py-12 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="bg-black text-white rounded-2xl sm:rounded-[24px] md:rounded-[40px] p-4 sm:p-6 md:p-8 lg:p-16 text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">Costs Go Down. Student Results Go Up.</h2>
            <p className="text-white/70 mb-8 max-w-2xl mx-auto">Pay per paper, not per person. The more you evaluate, the cheaper it gets. And students get better feedback.</p>
            <a href="/support" className="inline-block bg-white text-black text-[15px] font-bold rounded-full px-8 py-3.5 hover:scale-[1.03] transition-all duration-200">
              Talk to Our Team
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CoachingPage;
